import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import Device from '../../components/DeviceAsDom';
import NonDevice from '../../components/NonDevice/NonDevice';
import NON_DEVICES from '../../constants/NonDevices';
import PageLoading from '../../components/PageLoading/PageLoading';
import './DeviceDOMCanvas.css';
import { withTranslation } from 'react-i18next';
import {TemplatePlantConfigurator} from '../../components/TemplatePlantConfigurator/TemplatePlantConfigurator.tsx';
import {isFeatureEnabled} from '../../utils/feature-flags';

const FEATURE_PLANT_TEMPLATE_INITIALIZATION = isFeatureEnabled('plant_template_initialization');

class DeviceDOMCanvas extends Component {
  constructor (props) {
    super(props);
    this.state = {
      isSmallScreen: window.innerWidth < 768,
    };
    this.mounted = false;
  }

  onUpdateNonDevice = (id, params) => {
    return this.props.devicesStore.upsertNonDeviceGUI(id, params);
  };

  componentDidMount() {
    this.mounted = true;
    document.addEventListener('keydown', this.handleNonDeviceKeyPress);
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    this.mounted = false;
    document.removeEventListener('keydown', this.handleNonDeviceKeyPress);
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    if (this.mounted) {
      this.setState({
        isSmallScreen: window.innerWidth < 768,
      });
    }
  };

  handleNonDeviceKeyPress = e => {
    const key = e.keyCode;
    const realValue = String.fromCharCode(key);
    const ESC = 27;
    const F1 = 112;
    const F2 = 113;
    const F4 = 115;
    const F7 = 118;
    const F10 = 121;
    const { batchStore, sidebarStore } = this.props;

    batchStore.saveLog(`Key pressed ${realValue}, ${key}`);

    if ([ESC, F1, F2, F4, F7, F10].includes(key)) {
      e.preventDefault();
    }

    if (!batchStore.canEmit) {
      return;
    }

    if ([ESC, F1, F2, F4, F10].includes(key)
      && this[`time-${key}`] && this[`time-${key}`] + 1000 > new Date().getTime()) {
      return;
    }

    this[`time-${key}`] = new Date().getTime();

    switch (key) {
      case ESC:
        batchStore.abort();
        break;
      case F2:
        batchStore.override();
        break;
      case F4:
        batchStore.emergencyStop();
        break;
      case F7:
        sidebarStore.onToggle('batchForm');
        batchStore.checkScalesAndClose();
        break;
      case F10:
        batchStore.discharge(true);
        break;
      default:
    }
  };

  handleNonDeviceClick = id => {
    const { batchStore, sidebarStore } = this.props;

    if (!(batchStore.canEmit || batchStore.canSendRemoteInstruction())) {
      return;
    }

    if ((id === 'discharge' || id === 'holdfeed' || id === 'abort' || id === 'stop')
      && this[`time-${id}`] && this[`time-${id}`] + 1000 > new Date().getTime()) {
      return;
    }

    this[`time-${id}`] = new Date().getTime();

    switch (id) {
      case 'discharge':
        batchStore.discharge(true);
        break;
      case 'override':
        batchStore.override();
        break;
      case 'demo':
        batchStore.demo();
        break;
      case 'abort':
        batchStore.abort();
        break;
      case 'batch':
        sidebarStore.onToggle('batchForm');
        batchStore.checkScalesAndClose();
        break;
      case 'material':
        sidebarStore.onToggle('batchMaterial');
        break;
      case 'rateplus':
        batchStore.flowRateUp();
        break;
      case 'rateminus':
        batchStore.flowRateDown();
        break;
      case 'holdfeed':
        batchStore.holdFeed();
        break;
      case 'stop':
        batchStore.emergencyStop();
        break;
      case 'washout':
        sidebarStore.onToggle('washout');
        break;
      default:
    }
  };

  isActive = id => {
    const { batchStore } = this.props;
    let active = false;

    switch (id) {
      case 'discharge':
        active = batchStore.isDischarge && !batchStore.isHoldDischarge;
        break;
      case 'demo':
        active = batchStore.isDemo;
        break;
      case 'holdfeed':
        active = batchStore.isHoldFeed;
        break;
      case 'override':
        active = batchStore.isOverride;
        break;
      case 'abort':
        active = batchStore.isAbort;
        break;
      case 'stop':
        active = batchStore.isEmergencyStop;
        break;
      default:
    }

    return active;
  };

  canDrag() {
    const { configuration } = this.props.settingsStore;
    const { isSmallScreen } = this.state;

    return !isSmallScreen && !configuration.disableDragging;
  }

  renderDevice(device) {
    if (!device) {
      return;
    }

    const { devicesStore, materialStore } = this.props;
    const { isSmallScreen } = this.state;
    const gui = devicesStore.getDeviceGUIById(device.id, device.deviceTypeName);
    const DeviceComponent = Device[device.deviceTypeName];
    const isHidden = device.hide || materialStore.shouldHideInactiveBin(device.id)
      || (!materialStore.isMaterialForBinVisible(device.id) && devicesStore.isBinWithTheSameOutput(device));

    if (!DeviceComponent) {
      return null;
    }

    return isHidden ? '' : (
      <DeviceComponent
        className={`device-type--${device.deviceTypeName} device-id--${device.id} device-icon--${device.icon}`}
        canDrag={this.canDrag()}
        isSmallScreen={isSmallScreen}
        key={`device_${device.id}`}
        x={gui.x}
        y={gui.y}
        z={gui.z}
        width={gui.length}
        height={gui.size}
        device={device}
        onDelete={() =>
          devicesStore.confirmDeleteDevice(device.id, device.deviceTypeName)
        }
        onEdit={() => devicesStore.startEdit(device.id, device.deviceTypeName)}
        onUpdate={params => {
          devicesStore.upsertDeviceGUI(device.id, device.deviceTypeName, params, (gui) => {
            devicesStore.moveBinsWithTheSameOutput(device, gui);
          });
        }}
      />
    );
  }

  renderNonDevice(nonDevice) {
    const { devicesStore, batchStore, t } = this.props;

    const gui = devicesStore.getNonDeviceGUIById(nonDevice.id);

    const renderTitle = () => {
      if (nonDevice.id === 'discharge') {
        return batchStore.discharging && !batchStore.isHoldDischarge ? t('buttons.Hold Disch') : t('buttons.Discharge');
      }
      return t(`buttons.${nonDevice.title}`);
    };

    return (
      <NonDevice
        className={`non-device-${nonDevice.id}`}
        canDrag={this.canDrag()}
        active={this.isActive(nonDevice.id)}
        key={`non-device-${nonDevice.id}`}
        title={renderTitle()}
        icon={nonDevice.icon}
        shortKey={nonDevice.shortKey}
        x={gui.x}
        y={gui.y}
        z={gui.z}
        id={nonDevice.id}
        width={gui.length}
        height={gui.size}
        color={gui.color}
        background={gui.background}
        fontSize={gui.fontSize}
        onUpdate={params =>
          this.onUpdateNonDevice(nonDevice.id, params)
        }
        onClick={() => this.handleNonDeviceClick(nonDevice.id)}
      />
    );
  }

  render() {
    const { newAuthStore, devicesStore, materialStore } = this.props;
    const { devices, guiLoaded } = devicesStore;
    const { materialDetailsLoaded } = materialStore;

    if (!guiLoaded || !materialDetailsLoaded) return (<PageLoading />);

    const devicesSorted = devices
      ? devices.slice().sort((a, b) => (a.deviceTypeName > b.deviceTypeName) ? 1 : ((b.deviceTypeName > a.deviceTypeName) ? -1 : 0))
      : [];

    const showTemplatePlantConfigurator = (
      devicesSorted.length === 0 &&
      newAuthStore.user?.super &&
      FEATURE_PLANT_TEMPLATE_INITIALIZATION
    );

    return (
      <div className="device-dom-canvas">
        {showTemplatePlantConfigurator && (
          <TemplatePlantConfigurator />
        )}
        <div className="devices-wrapper">
          {devicesSorted.map(d => this.renderDevice(d))}
        </div>
        <div className="control-buttons">
          {NON_DEVICES.map(d => this.renderNonDevice(d))}
        </div>
      </div>
    );
  }
}

export default withTranslation()(inject('devicesStore', 'batchStore', 'sidebarStore', 'materialStore', 'settingsStore', 'newAuthStore')(observer(DeviceDOMCanvas)));
